import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { Updater } from "@tanstack/vue-table";
import { provide, ref, Ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { saveAs } from 'file-saver';
import { format } from 'date-fns/format';
import { formatDate as formatDateDF } from 'date-fns';
import { Locale } from 'date-fns/locale';
import { formatInTimeZone } from 'date-fns-tz';

import { AlignCenterIcon, AlignLeftIcon, AlignRightIcon, DotIcon, MinusIcon, MoreHorizontalIcon } from 'lucide-vue-next';
import { Template, TemplateBlock } from '@/components/shared/report-template/types/editor.ts';
import { useToast } from '@/components/ui/toast';
import en from '@/i18n/locales/en.json';
import km from '@/i18n/locales/km.json';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function valueUpdater<T extends Updater<any>>(updaterOrValue: T, ref: Ref) {
  ref.value
    = typeof updaterOrValue === 'function'
      ? updaterOrValue(ref.value)
      : updaterOrValue
}

export function jsonToCsv(items: any[]) {
  const header = Object.keys(items[0]);
  const headerString = header.join(',');
  // handle null or undefined values here
  const replacer = (key, value) => value ?? '';
  const rowItems = items.map((row) =>
    header
      .map((fieldName) => JSON.stringify(row[fieldName], replacer))
      .join(',')
  );
  // join header and body, and break into separate lines
  return [headerString, ...rowItems].join('\r\n');
}

export function useLocale() {
  const { locale } = useI18n()

  const setLocale = (lang: string) => {
    locale.value = lang
  }

  // Initialize from localStorage
  if (localStorage.getItem('locale')) {
    setLocale(localStorage.getItem('locale') || 'en')
  }

  // Save to localStorage when changed
  watch(locale, (newLocale) => {
    localStorage.setItem('locale', newLocale)
  })

  return { locale, setLocale }
};

export function setupProvide() {
  const isDialogCreateUpdateCourseOpen = ref(false);
  const isDialogAssignToClassOpen = ref(false);
  const isDialogCreateUpdateGradeOpen = ref(false);
  const isDialogCreateUpdateClassOpen = ref(false);
  const isDialogCreateUpdateRoomOpen = ref(false);
  const isDialogCreateUpdateTeacherOpen = ref(false);
  const isDialogUpdateTeacherOpen = ref(false);
  const isDialogAssignCourseTeacherOpen = ref(false);
  const isDialogChangeCourseTeacherOpen = ref(false);
  const isDialogProfileTeacherOpen = ref(false);
  const isDialogProfileAttachmentOpen = ref(false);
  const isDialogCreateCourseScheduleOpen = ref(false);
  const isDialogViewCourseScheduleOpen = ref(false);
  const isDialogViewAttendanceRequestUpdateOpen = ref(false);
  const isDialogCreateParentOpen = ref(false);
  const isDialogUpdateParentOpen = ref(false);
  const isDialogProfileParentOpen = ref(false);
  const isDialogProfileStudentOpen = ref(false);
  const isDialogCreateStudentOpen = ref(false);
  const isDialogUpdateStudentOpen = ref(false);
  const isDialogCreateUpdateLetterGradeOpen = ref(false);
  const isDialogCreateUpdateReportPeriodOpen = ref(false);
  const isDialogCreateUpdateGeneralExamOpen = ref(false);
  const isDialogCreateSubjectExamOpen = ref(false);
  const isDialogUpdateSubjectExamOpen = ref(false);
  const isDialogAddStudentToParentOrRemoveFromParentOpen = ref(false);
  const isDialogViewDetailLetterGradeOpen = ref(false);
  const isDialogViewDetailGeneralExamOpen = ref(false);
  const isDialogViewDetailSubjectExamOpen = ref(false);
  const isDialogCreateUpdateAcademicYearOpen = ref(false);
  const isDialogCreateUpdateRoleOpen = ref(false);
  const isDialogCreateUpdateBranchOpen = ref(false);
  const isDialogCreateUpdateShiftOpen = ref(false);
  const isDialogTransferStudentOpen = ref(false);
  const isDialogResetPasswordStudentOpen = ref(false);
  const isDialogResetPasswordTeacherOpen = ref(false);
  const isDialogResetPasswordParentOpen = ref(false);
  const isDialogStudentReportOpen = ref(false);
  const isDialogTeacherGradeCourseOpen = ref(false);
  const isDialogCreateUpdatePromotionCriteriaOpen = ref(false);
  const isDialogCreateUpdateQuizOpen = ref(false);

  provide('dialogCreateUpdateCourseState', {
    isOpen: isDialogCreateUpdateCourseOpen,
    open: () => {
      return isDialogCreateUpdateCourseOpen.value = true;
    },
    close: () => {
      return isDialogCreateUpdateCourseOpen.value = false;
    }
  });

  provide('dialogAssignToClassState', {
    isOpen: isDialogAssignToClassOpen,
    open: () => {
      return isDialogAssignToClassOpen.value = true;
    },
    close: () => {
      return isDialogAssignToClassOpen.value = false;
    }
  })
  provide('dialogCreateUpdateGradeState', {
    isOpen: isDialogCreateUpdateGradeOpen,
    open: () => {
      return isDialogCreateUpdateGradeOpen.value = true;
    },
    close: () => {
      return isDialogCreateUpdateGradeOpen.value = false;
    }
  })
  provide('dialogCreateUpdateQuizState', {
    isOpen: isDialogCreateUpdateQuizOpen,
    open: () => {
      return isDialogCreateUpdateQuizOpen.value = true;
    },
    close: () => {
      return isDialogCreateUpdateQuizOpen.value = false;
    }
  })
  provide('dialogCreateUpdateClassState', {
    isOpen: isDialogCreateUpdateClassOpen,
    open: () => {
      return isDialogCreateUpdateClassOpen.value = true;
    },
    close: () => {
      return isDialogCreateUpdateClassOpen.value = false;
    }
  })
  provide('dialogCreateUpdateRoomState', {
    isOpen: isDialogCreateUpdateRoomOpen,
    open: () => {
      return isDialogCreateUpdateRoomOpen.value = true;
    },
    close: () => {
      return isDialogCreateUpdateRoomOpen.value = false;
    }
  })

  provide('dialogCreateTeacherState', {
    isOpen: isDialogCreateUpdateTeacherOpen,
    open: () => {
      return isDialogCreateUpdateTeacherOpen.value = true;
    },
    close: () => {
      return isDialogCreateUpdateTeacherOpen.value = false;
    }
  })

  provide('dialogUpdateTeacherState', {
    isOpen: isDialogUpdateTeacherOpen,
    open: () => {
      return isDialogUpdateTeacherOpen.value = true;
    },
    close: () => {
      return isDialogUpdateTeacherOpen.value = false;
    }
  })

  provide('dialogAssignCourseTeacherState', {
    isOpen: isDialogAssignCourseTeacherOpen,
    open: () => {
      return isDialogAssignCourseTeacherOpen.value = true;
    },
    close: () => {
      return isDialogAssignCourseTeacherOpen.value = false;
    }
  })

  provide('dialogChangeCourseTeacherState', {
    isOpen: isDialogChangeCourseTeacherOpen,
    open: () => {
      return isDialogChangeCourseTeacherOpen.value = true;
    },
    close: () => {
      return isDialogChangeCourseTeacherOpen.value = false;
    }
  })

  provide('dialogProfileTeacherState', {
    isOpen: isDialogProfileTeacherOpen,
    open: () => {
      return isDialogProfileTeacherOpen.value = true;
    },
    close: () => {
      return isDialogProfileTeacherOpen.value = false;
    }
  })

  provide('dialogProfileAttachmentState', {
    isOpen: isDialogProfileAttachmentOpen,
    open: () => {
      return isDialogProfileAttachmentOpen.value = true;
    },
    close: () => {
      return isDialogProfileAttachmentOpen.value = false;
    }
  })

  provide('dialogCreateCourseScheduleState', {
    isOpen: isDialogCreateCourseScheduleOpen,
    open: () => {
      return isDialogCreateCourseScheduleOpen.value = true;
    },
    close: () => {
      return isDialogCreateCourseScheduleOpen.value = false;
    }
  })

  provide('dialogViewCourseScheduleState', {
    isOpen: isDialogViewCourseScheduleOpen,
    open: () => {
      return isDialogViewCourseScheduleOpen.value = true;
    },
    close: () => {
      return isDialogViewCourseScheduleOpen.value = false;
    }
  })

  provide('dialogViewAttendanceRequestUpdate', {
    isOpen: isDialogViewAttendanceRequestUpdateOpen,
    open: () => {
      return isDialogViewAttendanceRequestUpdateOpen.value = true;
    },
    close: () => {
      return isDialogViewAttendanceRequestUpdateOpen.value = false;
    }
  })

  provide('dialogCreateParentState', {
    isOpen: isDialogCreateParentOpen,
    open: () => {
      return isDialogCreateParentOpen.value = true;
    },
    close: () => {
      return isDialogCreateParentOpen.value = false;
    }
  })

  provide('dialogUpdateParentState', {
    isOpen: isDialogUpdateParentOpen,
    open: () => {
      return isDialogUpdateParentOpen.value = true;
    },
    close: () => {
      return isDialogUpdateParentOpen.value = false;
    }
  })

  provide('dialogProfileParentState', {
    isOpen: isDialogProfileParentOpen,
    open: () => {
      return isDialogProfileParentOpen.value = true;
    },
    close: () => {
      return isDialogProfileParentOpen.value = false;
    }
  })

  provide('dialogCreateStudentState', {
    isOpen: isDialogCreateStudentOpen,
    open: () => {
      return isDialogCreateStudentOpen.value = true;
    },
    close: () => {
      return isDialogCreateStudentOpen.value = false;
    }
  })

  provide('dialogUpdateStudentState', {
    isOpen: isDialogUpdateStudentOpen,
    open: () => {
      return isDialogUpdateStudentOpen.value = true;
    },
    close: () => {
      return isDialogUpdateStudentOpen.value = false;
    }
  })

  provide('dialogProfileStudentState', {
    isOpen: isDialogProfileStudentOpen,
    open: () => {
      return isDialogProfileStudentOpen.value = true;
    },
    close: () => {
      return isDialogProfileStudentOpen.value = false;
    }
  })

  provide('dialogCreateUpdateLetterGradeState', {
    isOpen: isDialogCreateUpdateLetterGradeOpen,
    open: () => {
      return isDialogCreateUpdateLetterGradeOpen.value = true;
    },
    close: () => {
      return isDialogCreateUpdateLetterGradeOpen.value = false;
    }
  })

  provide('dialogCreateUpdateReportPeriodState', {
    isOpen: isDialogCreateUpdateReportPeriodOpen,
    open: () => {
      return isDialogCreateUpdateReportPeriodOpen.value = true;
    },
    close: () => {
      return isDialogCreateUpdateReportPeriodOpen.value = false;
    }
  })

  provide('dialogCreateUpdateGeneralExamState', {
    isOpen: isDialogCreateUpdateGeneralExamOpen,
    open: () => {
      return isDialogCreateUpdateGeneralExamOpen.value = true;
    },
    close: () => {
      return isDialogCreateUpdateGeneralExamOpen.value = false;
    }
  })

  provide('dialogCreateSubjectExamState', {
    isOpen: isDialogCreateSubjectExamOpen,
    open: () => {
      return isDialogCreateSubjectExamOpen.value = true;
    },
    close: () => {
      return isDialogCreateSubjectExamOpen.value = false;
    }
  })

  provide('dialogUpdateSubjectExamState', {
    isOpen: isDialogUpdateSubjectExamOpen,
    open: () => {
      return isDialogUpdateSubjectExamOpen.value = true;
    },
    close: () => {
      return isDialogUpdateSubjectExamOpen.value = false;
    }
  })

  provide('dialogAddStudentToParentOrRemoveFromParentState', {
    isOpen: isDialogAddStudentToParentOrRemoveFromParentOpen,
    open: () => {
      return isDialogAddStudentToParentOrRemoveFromParentOpen.value = true;
    },
    close: () => {
      return isDialogAddStudentToParentOrRemoveFromParentOpen.value = false;
    }
  })

  provide('dialogViewDetailLetterGradeState', {
    isOpen: isDialogViewDetailLetterGradeOpen,
    open: () => {
      return isDialogViewDetailLetterGradeOpen.value = true;
    },
    close: () => {
      return isDialogViewDetailLetterGradeOpen.value = false;
    }
  })

  provide('dialogViewDetailGeneralExamState', {
    isOpen: isDialogViewDetailGeneralExamOpen,
    open: () => {
      return isDialogViewDetailGeneralExamOpen.value = true;
    },
    close: () => {
      return isDialogViewDetailGeneralExamOpen.value = false;
    }
  })

  provide('dialogViewDetailSubjectExamState', {
    isOpen: isDialogViewDetailSubjectExamOpen,
    open: () => {
      return isDialogViewDetailSubjectExamOpen.value = true;
    },
    close: () => {
      return isDialogViewDetailSubjectExamOpen.value = false;
    }
  })

  provide('dialogCreateUpdateAcademicYearState', {
    isOpen: isDialogCreateUpdateAcademicYearOpen,
    open: () => {
      return isDialogCreateUpdateAcademicYearOpen.value = true;
    },
    close: () => {
      return isDialogCreateUpdateAcademicYearOpen.value = false;
    }
  })

  provide('dialogCreateUpdateRoleState', {
    isOpen: isDialogCreateUpdateRoleOpen,
    open: () => {
      return isDialogCreateUpdateRoleOpen.value = true;
    },
    close: () => {
      return isDialogCreateUpdateRoleOpen.value = false;
    }
  })

  provide('dialogCreateUpdateBranchState', {
    isOpen: isDialogCreateUpdateBranchOpen,
    open: () => {
      return isDialogCreateUpdateBranchOpen.value = true;
    },
    close: () => {
      return isDialogCreateUpdateBranchOpen.value = false;
    }
  })

  provide('dialogCreateUpdateShiftState', {
    isOpen: isDialogCreateUpdateShiftOpen,
    open: () => {
      return isDialogCreateUpdateShiftOpen.value = true;
    },
    close: () => {
      return isDialogCreateUpdateShiftOpen.value = false;
    }
  })

  provide('dialogTransferStudentState', {
    isOpen: isDialogTransferStudentOpen,
    open: () => {
      return isDialogTransferStudentOpen.value = true;
    },
    close: () => {
      return isDialogTransferStudentOpen.value = false;
    }
  })

  provide('dialogResetPasswordStudentState', {
    isOpen: isDialogResetPasswordStudentOpen,
    open: () => { return isDialogResetPasswordStudentOpen.value = true; },
    close: () => { return isDialogResetPasswordStudentOpen.value = false; }
  })

  provide('dialogResetPasswordTeacherState', {
    isOpen: isDialogResetPasswordTeacherOpen,
    open: () => { return isDialogResetPasswordTeacherOpen.value = true; },
    close: () => { return isDialogResetPasswordTeacherOpen.value = false; }
  })

  provide('dialogResetPasswordParentState', {
    isOpen: isDialogResetPasswordParentOpen,
    open: () => { return isDialogResetPasswordParentOpen.value = true; },
    close: () => { return isDialogResetPasswordParentOpen.value = false; }
  })

  provide('dialogStudentReportState', {
    isOpen: isDialogStudentReportOpen,
    open: () => { return isDialogStudentReportOpen.value = true; },
    close: () => { return isDialogStudentReportOpen.value = false; }
  })

  provide('dialogTeacherGradeCourseState', {
    isOpen: isDialogTeacherGradeCourseOpen,
    open: () => { return isDialogTeacherGradeCourseOpen.value = true; },
    close: () => { return isDialogTeacherGradeCourseOpen.value = false; }
  })

  provide('dialogCreateUpdatePromotionCriteriaState', {
    isOpen: isDialogCreateUpdatePromotionCriteriaOpen,
    open: () => { return isDialogCreateUpdatePromotionCriteriaOpen.value = true; },
    close: () => { return isDialogCreateUpdatePromotionCriteriaOpen.value = false; }
  })
}

// Lazy-loaded Excel functions
export async function exportExcel(data: any, fileName: string, sheetName: string) {
  const { exportExcel: exportExcelImpl } = await import('@/lib/excel-utils');
  return exportExcelImpl(data, fileName, sheetName);
}

export async function readExcelFile(buffer: ArrayBuffer): Promise<any[]> {
  const { readExcelFile: readExcelFileImpl } = await import('@/lib/excel-utils');
  return readExcelFileImpl(buffer);
}

export async function readParentExcelFile(buffer: ArrayBuffer, headerRow: number = 1, dataStartRow: number = 6): Promise<any[]> {
  const { readParentExcelFile: readParentExcelFileImpl } = await import('@/lib/excel-utils');
  return readParentExcelFileImpl(buffer, headerRow, dataStartRow);
}

export async function readStudentExcelFile(buffer: ArrayBuffer, headerRow: number = 1, dataStartRow: number = 6): Promise<any[]> {
  const { readStudentExcelFile: readStudentExcelFileImpl } = await import('@/lib/excel-utils');
  return readStudentExcelFileImpl(buffer, headerRow, dataStartRow);
}

export async function readTeacherExcelFile(buffer: ArrayBuffer, headerRow: number = 1, dataStartRow: number = 6): Promise<any[]> {
  const { readTeacherExcelFile: readTeacherExcelFileImpl } = await import('@/lib/excel-utils');
  return readTeacherExcelFileImpl(buffer, headerRow, dataStartRow);
}

export const getEffectiveTimezone = (branchTimezone?: string | null): string => {
  if (branchTimezone) return branchTimezone;

  try {
    const branch = JSON.parse(localStorage.getItem('branch') || '{}');
    if (branch?.timezone) return branch.timezone;

    const school = JSON.parse(localStorage.getItem('school') || '{}');
    // Older schools were provisioned with UTC as a placeholder even though
    // school clock times use Cambodia time by default. An explicit branch
    // timezone still wins above, including a deliberate UTC branch.
    if (school?.preference?.timezone && school.preference.timezone !== 'UTC') {
      return school.preference.timezone;
    }
  } catch (_) {
    // Use the same deterministic fallback as the backend.
  }

  return 'Asia/Phnom_Penh';
};

export const formatDate = (date: Date | null, formatString: string, locale: Locale) => {
  if (!date) return '';
  return formatInTimeZone(date, getEffectiveTimezone(), formatString, { locale: locale });
};

export const formatInstantWithTimezone = (
  value: string | Date,
  formatString: string = 'yyyy-MM-dd HH:mm',
  branchTimezone?: string | null,
): string => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return formatInTimeZone(date, getEffectiveTimezone(branchTimezone), formatString);
};

/** Locale-aware instant formatting with the branch timezone forced explicitly. */
export const formatInstantLocale = (
  value: string | number | Date,
  locales?: Intl.LocalesArgument,
  options: Intl.DateTimeFormatOptions = {},
  branchTimezone?: string | null,
): string => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(locales, {
    ...options,
    timeZone: getEffectiveTimezone(branchTimezone),
  }).format(date);
};

/** Returns today's civil date in the timezone selected by core for the branch. */
export const branchToday = (branchTimezone?: string | null): string =>
  formatInstantWithTimezone(new Date(), 'yyyy-MM-dd', branchTimezone);

/**
 * Formats a DATE column without interpreting it as a UTC instant. This avoids
 * moving values such as 2026-09-13 to the previous day in western timezones.
 */
export const formatCalendarDate = (
  value: string | Date | null | undefined,
  formatString: string = 'yyyy-MM-dd',
  locale?: Locale,
): string => {
  if (!value) return '';
  if (value instanceof Date) {
    return formatDateDF(
      new Date(value.getFullYear(), value.getMonth(), value.getDate(), 12),
      formatString,
      locale ? { locale } : undefined,
    );
  }
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return value;
  return formatDateDF(
    new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12),
    formatString,
    locale ? { locale } : undefined,
  );
};

/** Returns a branch calendar date offset without consulting the browser date. */
export const branchDateOffset = (days: number): string => {
  const [year, month, day] = branchToday().split('-').map(Number);
  const shifted = new Date(year, month - 1, day + days, 12);
  return formatDateDF(shifted, 'yyyy-MM-dd');
};

/**
 * Converts a shift's UTC-clock TIME value into its branch-local clock value.
 * This helper is shift-specific; timetable/course schedule TIME values are
 * already branch-local and must not use it.
 * @param time - UTC clock string, e.g. "00:00:00"
 * @param fmt - Output format, defaults to "HH:mm"
 * @returns The branch-local clock value, e.g. "08:00"
 */
export const formatTimeWithTimezone = (time: string | null | undefined, fmt: string = 'HH:mm', branchTimezone?: string): string => {
  if (!time) return '';
  const match = time.match(/(?:T|^)(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!match) return time;
  const reference = new Date();
  const utcClock = new Date(Date.UTC(
    reference.getUTCFullYear(),
    reference.getUTCMonth(),
    reference.getUTCDate(),
    Number(match[1]),
    Number(match[2]),
    Number(match[3] || 0),
  ));
  return formatInTimeZone(utcClock, getEffectiveTimezone(branchTimezone), fmt);
};

/**
 * Shared timezone options for use across all forms (registration, settings, etc.)
 * Dynamically generated from the browser's full IANA timezone database.
 */
export const TIMEZONE_OPTIONS: ReadonlyArray<{ id: string; label: string; value: string }> = (() => {
  try {
    const zones = Intl.supportedValuesOf('timeZone');
    return zones.map((tz, i) => {
      // Build a human-readable offset label
      const now = new Date();
      const formatter = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' });
      const parts = formatter.formatToParts(now);
      const offsetPart = parts.find(p => p.type === 'timeZoneName');
      const offset = offsetPart?.value || '';
      return { id: String(i + 1), label: `${tz} (${offset})`, value: tz };
    });
  } catch {
    // Fallback for older browsers that don't support supportedValuesOf
    return [
      { id: '1', label: 'Asia/Phnom_Penh (GMT+7)', value: 'Asia/Phnom_Penh' },
      { id: '2', label: 'Asia/Singapore (GMT+8)', value: 'Asia/Singapore' },
      { id: '3', label: 'America/New_York (GMT-5)', value: 'America/New_York' },
      { id: '4', label: 'Europe/London (GMT+0)', value: 'Europe/London' },
      { id: '5', label: 'UTC', value: 'UTC' },
    ];
  }
})();

/**
 * Shared country code options for phone number inputs across all forms.
 * Cambodia is listed first as the primary user base, followed by regional neighbors,
 * then other major countries alphabetically.
 */
export const COUNTRY_CODE_OPTIONS = [
  // Primary & Southeast Asia
  { code: '+855', name: 'Cambodia', flag: '🇰🇭' },
  { code: '+856', name: 'Laos', flag: '🇱🇦' },
  { code: '+95', name: 'Myanmar', flag: '🇲🇲' },
  { code: '+60', name: 'Malaysia', flag: '🇲🇾' },
  { code: '+65', name: 'Singapore', flag: '🇸🇬' },
  { code: '+62', name: 'Indonesia', flag: '🇮🇩' },
  { code: '+63', name: 'Philippines', flag: '🇵🇭' },
  // East Asia
  { code: '+86', name: 'China', flag: '🇨🇳' },
  { code: '+81', name: 'Japan', flag: '🇯🇵' },
  { code: '+82', name: 'South Korea', flag: '🇰🇷' },
  { code: '+886', name: 'Taiwan', flag: '🇹🇼' },
  { code: '+852', name: 'Hong Kong', flag: '🇭🇰' },
  // South Asia
  { code: '+91', name: 'India', flag: '🇮🇳' },
  { code: '+92', name: 'Pakistan', flag: '🇵🇰' },
  { code: '+880', name: 'Bangladesh', flag: '🇧🇩' },
  { code: '+94', name: 'Sri Lanka', flag: '🇱🇰' },
  { code: '+977', name: 'Nepal', flag: '🇳🇵' },
  // Middle East
  { code: '+971', name: 'United Arab Emirates', flag: '🇦🇪' },
  { code: '+966', name: 'Saudi Arabia', flag: '🇸🇦' },
  { code: '+974', name: 'Qatar', flag: '🇶🇦' },
  { code: '+972', name: 'Israel', flag: '🇮🇱' },
  { code: '+90', name: 'Turkey', flag: '🇹🇷' },
  // Europe
  { code: '+44', name: 'United Kingdom', flag: '🇬🇧' },
  { code: '+33', name: 'France', flag: '🇫🇷' },
  { code: '+49', name: 'Germany', flag: '🇩🇪' },
  { code: '+39', name: 'Italy', flag: '🇮🇹' },
  { code: '+34', name: 'Spain', flag: '🇪🇸' },
  { code: '+31', name: 'Netherlands', flag: '🇳🇱' },
  { code: '+46', name: 'Sweden', flag: '🇸🇪' },
  { code: '+41', name: 'Switzerland', flag: '🇨🇭' },
  { code: '+7', name: 'Russia', flag: '🇷🇺' },
  // Americas
  { code: '+1', name: 'United States', flag: '🇺🇸' },
  { code: '+1', name: 'Canada', flag: '🇨🇦' },
  { code: '+52', name: 'Mexico', flag: '🇲🇽' },
  { code: '+55', name: 'Brazil', flag: '🇧🇷' },
  { code: '+54', name: 'Argentina', flag: '🇦🇷' },
  { code: '+57', name: 'Colombia', flag: '🇨🇴' },
  // Oceania
  { code: '+61', name: 'Australia', flag: '🇦🇺' },
  { code: '+64', name: 'New Zealand', flag: '🇳🇿' },
  // Africa
  { code: '+20', name: 'Egypt', flag: '🇪🇬' },
  { code: '+234', name: 'Nigeria', flag: '🇳🇬' },
  { code: '+27', name: 'South Africa', flag: '🇿🇦' },
  { code: '+254', name: 'Kenya', flag: '🇰🇪' },
] as const;

/**
 * Converts a timestamp into a user-friendly relative time string
 * @param {Date|string|number} timestamp - The timestamp to convert (Date object, ISO string, or milliseconds)
 * @returns {string} - Relative time string (e.g., "just now", "5 minutes ago", "yesterday")
 */
export const getRelativeTime = (timestamp: Date | string | number, t: any) => {
  // Convert input to Date object if it's not already
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  const now = new Date();

  // Calculate the difference in milliseconds
  const diffMs = now.getTime() - date.getTime();
  if (diffMs < 0) {
    return t('relative_time.future'); // Handle future dates
  }

  // Convert to seconds
  const diffSec = Math.floor(diffMs / 1000);

  // Less than a minute
  if (diffSec < 60) {
    return t('relative_time.now');
  }

  // Minutes
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) {
    return diffMin === 1 ? `1 ${t('relative_time.minute')}` : `${diffMin} ${t('relative_time.minute')}`;
  }

  // Hours
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) {
    return diffHour === 1 ? `1 ${t('relative_time.hour')}` : `${diffHour} ${t('relative_time.hour')}`;
  }

  // Check if it's yesterday
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()) {
    return "yesterday";
  }

  // Days
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 7) {
    return diffDay === 1 ? `1 ${t('relative_time.day')}` : `${diffDay} ${t('relative_time.day')}`;
  }

  // Weeks
  const diffWeek = Math.floor(diffDay / 7);
  if (diffWeek < 4) {
    return diffWeek === 1 ? `1 ${t('relative_time.week')}` : `${diffWeek} ${t('relative_time.week')}`;
  }

  // Months
  const diffMonth = Math.floor(diffDay / 30);
  if (diffMonth < 12) {
    return diffMonth === 1 ? `1 ${t('relative_time.month')}` : `${diffMonth} ${t('relative_time.month')}`;
  }

  // Years
  const diffYear = Math.floor(diffDay / 365);
  return diffYear === 1 ? `1 ${t('relative_time.year')}` : `${diffYear} ${t('relative_time.year')}`;
};

export const gendersRecord = (t: any) => {
  return [{ value: 'Male', display: t('male') }, { value: 'Female', display: t('female') }];
};

export const dayOfWeekRecord = (t: any) => {
  return [
    { value: 1, display: t('monday') },
    { value: 2, display: t('tuesday') },
    { value: 3, display: t('wednesday') },
    { value: 4, display: t('thursday') },
    { value: 5, display: t('friday') },
    { value: 6, display: t('saturday') },
    { value: 0, display: t('sunday') },
  ];
};

export const monthRecord = (t: any) => {
  return [
    { id: 1, value: 'January', display: t('month.january') },
    { id: 2, value: 'February', display: t('month.february') },
    { id: 3, value: 'March', display: t('month.march') },
    { id: 4, value: 'April', display: t('month.april') },
    { id: 5, value: 'May', display: t('month.may') },
    { id: 6, value: 'June', display: t('month.june') },
    { id: 7, value: 'July', display: t('month.july') },
    { id: 8, value: 'August', display: t('month.august') },
    { id: 9, value: 'September', display: t('month.september') },
    { id: 10, value: 'October', display: t('month.october') },
    { id: 11, value: 'November', display: t('month.november') },
    { id: 12, value: 'December', display: t('month.december') },
  ];
};

export const shiftRecord = (t: any) => {
  return [
    { id: 1, value: 'morning', display: t('shift.morning') },
    { id: 2, value: 'afternoon', display: t('shift.afternoon') },
    { id: 3, value: 'full', display: t('shift.full_day') },
  ]
}

export const borderStyle = () => {
  return {
    top: { style: 'thin' },
    left: { style: 'thin' },
    bottom: { style: 'thin' },
    right: { style: 'thin' }
  }
}

export const excelDateToJSDate = (serial: number) => {
  const utc_days = Math.floor(serial - 25569);
  const utc_value = utc_days * 86400;
  const date_info = new Date(utc_value * 1000);

  const fractional_day = serial - Math.floor(serial) + 0.0000001;

  let total_seconds = Math.floor(86400 * fractional_day);

  const seconds = total_seconds % 60;

  total_seconds -= seconds;

  const hours = Math.floor(total_seconds / (60 * 60));
  const minutes = Math.floor(total_seconds / 60) % 60;

  return formatDateDF(new Date(date_info.getFullYear(), date_info.getMonth(), date_info.getDate(), hours, minutes, seconds), 'yyyy-MM-dd');
}

export const convertToExcelDate = (jsDate: Date) => {
  // Excel's epoch starts on January 1, 1900
  const excelEpoch = new Date(1899, 11, 30);

  // Calculate the number of days between the date and Excel's epoch
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  return Math.round((jsDate.getTime() - excelEpoch.getTime()) / millisecondsPerDay);
}

// Lazy-loaded Excel utility functions
export async function applyOuterBorder(worksheet: any, startRow: number, startCol: number, endRow: number, endCol: number, borderStyle: any, excludeColumns: Array<number> = []) {
  const { applyOuterBorder: applyOuterBorderImpl } = await import('@/lib/excel-utils');
  return applyOuterBorderImpl(worksheet, startRow, startCol, endRow, endCol, borderStyle, excludeColumns);
}

export async function applyFormat(worksheet: any, startRow: number, startCol: number, endRow: number, endCol: number, format?: string, font?: string, bold?: boolean) {
  const { applyFormat: applyFormatImpl } = await import('@/lib/excel-utils');
  return applyFormatImpl(worksheet, startRow, startCol, endRow, endCol, format, font, bold);
}

// Lazy-loaded Excel creation functions
export async function createExcelWorkbook(sheetName: string, showGridLines: boolean = true) {
  const { createExcelWorkbook: createExcelWorkbookImpl } = await import('@/lib/excel-utils');
  return createExcelWorkbookImpl(sheetName, showGridLines);
}

// Callers do t(err.key, err.params); vue-i18n throws "Invalid arguments" on an
// explicit undefined second arg, so params always defaults to {}.
export function mapBackendError(error: unknown): { key: string; message?: string; params: any } {
  const mapped = mapBackendErrorInternal(error);
  return { ...mapped, params: mapped.params ?? {} };
}

function mapBackendErrorInternal(error: unknown): { key: string; message?: string; params?: any } {
  // Stores sometimes rethrow an already mapped error.
  if (error && typeof error === 'object' && 'key' in error && typeof error.key === 'string') {
    return error as { key: string; message?: string; params?: any };
  }
  const record = (value: unknown): Record<string, any> =>
    value !== null && typeof value === 'object' ? value as Record<string, any> : {};
  const text = (value: unknown): string => typeof value === 'string' ? value.trim() : '';
  const input = record(error);
  const response = record(input.response);
  const body = response.data ?? error;
  const payload = record(body);
  const nestedError = record(payload.error);
  const errorMessage = text(body) || text(payload.message) || text(payload.error) || text(nestedError.message);
  const status = response.status ?? input.status;
  const code = text(payload.error_code) || text(payload.code);
  if (code === 'COURSE_ALREADY_ASSIGNED_TO_CLASS') {
    const params = record(payload.error_params);
    return {
      key: 'apiErrors.COURSE_ALREADY_ASSIGNED_TO_CLASS',
      params: { courseId: params.course_id, classId: params.class_id },
    };
  }
  if (code === 'COURSE_NOT_ASSIGNED_TO_CLASS') {
    const match = /^course (\d+) is not assigned to class (\d+)/i.exec(errorMessage);
    if (match) return {
      key: 'apiErrors.COURSE_NOT_ASSIGNED_TO_CLASS',
      params: { courseId: match[1], classId: match[2] },
    };
  }
  if (code === 'PARENT_PHONE_DUPLICATE' || code === 'PARENT_NAME_DUPLICATE') {
    return { key: `apiErrors.${code}` };
  }
  if (code === 'PASSWORD_TOO_SHORT') {
    const params = record(payload.error_params);
    const minLength = params.min_length ?? /password must be at least (\d+) characters/i.exec(errorMessage)?.[1];
    return minLength
      ? { key: 'apiErrors.PASSWORD_TOO_SHORT', params: { minLength } }
      : { key: 'apiErrors.VALIDATION_ERROR' };
  }
  // Old servers reported this business conflict as INTERNAL_ERROR/500.
  const legacyAssignment = /^course (\d+) is already assigned to class (\d+)$/i.exec(errorMessage);
  if (legacyAssignment) {
    return {
      key: 'apiErrors.COURSE_ALREADY_ASSIGNED_TO_CLASS',
      params: { courseId: legacyAssignment[1], classId: legacyAssignment[2] },
    };
  }
  if (code && Object.prototype.hasOwnProperty.call(en.apiErrors, code)
    && Object.prototype.hasOwnProperty.call(km.apiErrors, code)) return { key: `apiErrors.${code}` };
  const displayMessage = (message: string) => ({
    key: 'errors.backendMessage', message, params: { message },
  });
  // Calendar constraint messages deliberately include the allowed range.
  const calendarMessage = errorMessage.match(/(?:Academic year requires|Academic year dates must|Reporting period requires|Reporting period changes must|Reporting period end date|Reporting period ".*?" needs valid dates|Child reporting period dates|Parent period dates|Parent period must retain|Parent reporting period must|Exam event requires|Exam event dates must|Exam event changes must|Exam class and event must|Exam date must|Select an existing academic year)[\s\S]*?(?= \(SQLSTATE|$)/)?.[0];
  if (calendarMessage) return displayMessage(calendarMessage);

  const errorMap = [
    {
      code: 'SHIFT_IN_USE',
      contains: 'shift is in use',
      handler: () => ({ key: 'shift_in_use' }),
    },
    {
      code: 'SHIFT_NOT_FOUND',
      contains: 'shift not found or not in this branch',
      handler: () => ({ key: 'shift_not_found' }),
    },
    {
      // Placement-test enrollment gate (mes-core ErrPlacementRequired). Surfaces
      // the raw backend detail (which grade is recommended, pass/fail) so staff
      // know to record a result, pick the recommended grade, or waive.
      code: 'PLACEMENT_TEST_REQUIRED',
      contains: 'placement test',
      handler: (msg: string) => ({
        key: 'errors.placementTestRequired',
        message: msg,
        params: { detail: msg },
      }),
    },
    {
      code: 'TEACHER_NAME_DUPLICATE',
      contains: 'duplicate key value violates unique constraint "teachers_name"',
      handler: (msg: string) => {
        const match = msg.match(/error creating user (.*?) teacher:/);
        const name = match ? match[1] : 'The teacher';
        return {
          key: 'errors.duplicateName',
          params: { name }
        };
      }
    },
    {
      code: 'TEACHER_COURSE_ALREADY_ASSIGNED',
      contains: 'teacher already assigned to this course',
      handler: () => {
        return {
          key: 'errors.thisCourseAlreadyAssign',
        }
      }
    },
    {
      code: 'COURSE_NOT_ASSIGNED_TO_CLASS',
      contains: 'is not assigned to class',
      handler: () => ({
        key: 'errors.courseNotAssignedToClass',
      })
    },
    {
      code: 'SESSION_LENGTH_INVALID',
      contains: 'multiple of 15',
      handler: () => ({
        key: 'errors.sessionLengthMultipleOf15',
      })
    },
    {
      code: 'TEACHER_CLASS_COURSE_DUPLICATE',
      contains: 'duplicate key value violates unique constraint "teachers_class_courses_pk"',
      handler: () => ({
        key: 'errors.duplicateTeacherCourse'
      })
    },
    {
      code: 'TEACHER_AVAILABILITY_DUPLICATE',
      contains: 'duplicate key value violates unique constraint "teacher_availability_pk_2"',
      handler: () => ({
        key: 'apiErrors.TEACHER_AVAILABILITY_DUPLICATE'
      })
    },
    {
      code: 'PARENT_PHONE_DUPLICATE',
      contains: 'duplicate key value violates unique constraint "parents_pk_phone_number"',
      handler: (msg: string) => {
        const match = msg.match(/error creating user (.*?) teacher:/);
        const name = match ? match[1] : 'The teacher';
        return {
          key: 'errors.duplicatePhoneNumber',
          params: { name }
        }
      }
    },
    {
      code: 'PARENT_NAME_DUPLICATE',
      contains: 'duplicate key value violates unique constraint "parents_pk_name"',
      handler: (msg: string) => {
        const match = msg.match(/error creating user (.*?) teacher:/);
        const name = match ? match[1] : 'The teacher';
        return {
          key: 'apiErrors.PARENT_NAME_DUPLICATE',
          params: { name }
        };
      }
    },
    {
      code: 'USER_PHONE_DUPLICATE',
      contains: 'user already registered with this phone number',
      handler: (msg: string) => {
        const match = msg.match(/registered with this phone number (\S+) and school (?:ID|code):/);
        const phoneNumber = match ? match[1] : 'Phone number';
        return {
          key: 'errors.userAlreadyRegistered',
          params: { phoneNumber }
        }
      }
    },
    {
      code: 'SCHEDULING_CONFLICT',
      contains: 'scheduling conflict detected',
      handler: () => {
        return {
          key: 'errors.duplicateSchedulingConflictDetected',
        }
      }
    },
    {
      code: 'LETTER_GRADE_REPORT_PERIOD_CONFLICT',
      contains: 'violates foreign key constraint "letter_grades_reporting_periods_id_fk"',
      handler: () => {
        return {
          key: 'errors.scoreTypeAndReportPeriod',
        }
      }
    },
    {
      code: 'REPORTING_PERIOD_NAME_DUPLICATE',
      contains: 'duplicate key value violates unique constraint "reporting_periods_display_name_uindex"',
      handler: (msg: string) => {
        const match = msg.match(/this display_name (.*?):/);
        const name = match ? match[1] : 'This name';
        return {
          key: 'errors.duplicateName',
          params: { name }
        };
      }
    },
    {
      code: 'EXAM_TIME_OVERLAP',
      contains: 'time of exam is overlaps',
      handler: () => {
        return {
          key: 'errors.examTimeOverlaps'
        }
      }
    },
    {
      code: 'STUDENT_SCORE_ALREADY_ASSIGNED',
      contains: 'student score already assigned',
      handler: () => {
        return {
          key: 'errors.studentScoreAlreadyAssign',
        }
      }
    },
    {
      code: 'COURSE_SCORE_LETTER_GRADE_MISSING',
      contains: 'no matching letter grade found for course score',
      handler: (msg: string) => {
        const match = msg.match(/no matching letter grade found for course score (\d+)/);
        const score = match ? match[1] : '';
        return {
          key: 'errors.noLetterGradeForCourseScore',
          params: { score }
        };
      }
    },
    {
      code: 'AVERAGE_SCORE_LETTER_GRADE_MISSING',
      contains: 'no matching letter grade found for average score',
      handler: (msg: string) => {
        const match = msg.match(/no matching letter grade found for average score (\d+)/);
        const score = match ? match[1] : '';
        return {
          key: 'errors.noLetterGradeForAverageScore',
          params: { score }
        };
      }
    },
    {
      code: 'EXAM_DATE_OUTSIDE_RANGE',
      contains: 'exam date must be within the global exam\'s date range',
      handler: () => {
        return {
          key: 'errors.examDateRange',
        }
      }
    },
    {
      code: 'SUBJECT_EXAMS_NOT_COMPLETED',
      contains: 'subject exams not completed',
      handler: (msg: string) => {
        const match = msg.match(/subject exams not completed\s*: '(.*?)'/);
        const examName = match ? match[1] : '';
        return {
          key: 'errors.subjectExamNotCompleted',
          params: { examName }
        }
      }
    },
    {
      code: 'SUBJECT_EXAMS_CANNOT_COMPLETE',
      contains: 'subject exams can not completed',
      handler: () => {
        return {
          key: 'errors.subjectExamCanNotCompleted',
        }
      }
    },
    {
      code: 'STUDENT_SCORE_NOT_ASSIGNED',
      contains: 'student score not yet assigned',
      handler: () => {
        return {
          key: 'errors.studentScoreNotYetAssigned',
        }
      }
    },
    {
      code: 'SESSION_NOT_FOUND',
      contains: 'No such session',
      handler: () => {
        return {
          key: 'errors.noSuchSession',
        }
      }
    },
    {
      code: 'QR_CODE_NOT_FOUND',
      contains: 'QR code: no rows in result set',
      handler: () => {
        return {
          key: 'errors.noQRCode',
        }
      }
    },
    {
      code: 'LETTER_GRADE_DUPLICATE',
      contains: 'duplicate key value violates unique constraint "letter_grades_pk_2"',
      handler: () => {
        return {
          key: 'errors.letter_grade_duplicate',
        }
      }
    },
    {
      code: 'INSUFFICIENT_ROLE_PERMISSIONS',
      contains: 'Insufficient role permissions',
      handler: () => {
        return {
          key: 'errors.insufficient_permissions',
        }
      }
    },
    {
      code: 'SCHOOL_ACCESS_DENIED',
      contains: 'You don\'t have access to this school',
      handler: () => {
        return {
          key: 'errors.no_access_to_school',
        }
      }
    },
    {
      code: 'SUBSCRIPTION_NOT_ACTIVE',
      contains: 'no active trial and subscription found for this school',
      handler: () => {
        return {
          key: 'errors.no_active_trial_subscription',
        }
      }
    },
    {
      code: 'INVALID_CREDENTIALS',
      contains: 'Invalid credentials',
      handler: () => {
        return {
          key: 'errors.invalid_credentials',
        }
      }
    },
    {
      contains: 'Unauthorized',
      handler: () => {
        return {
          key: 'errors.unauthorized',
        }
      }
    },
    {
      code: 'INVALID_USERNAME_OR_PASSWORD',
      contains: 'Invalid username or password',
      handler: () => {
        return {
          key: 'errors.invalid_username_password',
        }
      }
    },
    {
      code: 'MULTIPLE_TRIAL_ATTEMPTS',
      contains: 'trial request blocked: Multiple trial attempts detected from this account or device',
      handler: () => {
        return {
          key: 'errors.multiple_trial_attempts_detected',
        }
      }
    },
    {
      code: 'MULTIPLE_TRIAL_ATTEMPTS',
      contains: 'Multiple trial attempts detected',
      handler: () => {
        return {
          key: 'errors.multiple_trial_attempts_detected',
        }
      }
    },
    {
      code: 'TRIAL_REQUEST_BLOCKED',
      contains: 'trial request blocked',
      handler: (msg: string) => {
        // Extract more specific reason if available
        if (msg.includes('Multiple trial attempts detected')) {
          return {
            key: 'errors.multiple_trial_attempts_detected',
          }
        }
        return {
          key: 'errors.trial_request_blocked',
        }
      }
    }
    // Add more mappings as needed
  ];

  if (code) {
    for (const mapping of errorMap) {
      if ('code' in mapping && mapping.code === code) return mapping.handler(errorMessage);
    }
  }

  for (const mapping of errorMap) {
    if (errorMessage.includes(mapping.contains)) {
      const result = mapping.handler(errorMessage);
      return result;
    }
  }

  if (code) {
    const categoryCodes = new Set([
      'INVALID_REQUEST', 'AUTHENTICATION_REQUIRED', 'PAYMENT_REQUIRED',
      'AUTHORIZATION_FAILED', 'RESOURCE_NOT_FOUND', 'METHOD_NOT_ALLOWED',
      'RESOURCE_CONFLICT', 'VALIDATION_ERROR', 'RATE_LIMITED',
      'SERVICE_UNAVAILABLE', 'INTERNAL_ERROR', 'REQUEST_FAILED',
    ]);
    return { key: `apiErrors.${categoryCodes.has(code) ? code : 'REQUEST_FAILED'}` };
  }
  if (status === 401) return { key: 'errors.unauthorized' };

  // Keep raw database, transport and proxy diagnostics out of toasts.
  const technical = /SQLSTATE|duplicate key value|violates .*constraint|no rows in result set|sql:|pq:|pgx:|postgres(?:ql)?|panic:|goroutine |stack trace|dial tcp|connection refused|<[^>]+>|Request failed with status code|Network Error|timeout of \d+ms exceeded/i;
  if (errorMessage && !technical.test(errorMessage)) return displayMessage(errorMessage);
  return { key: 'errors.unexpected' };
}

export const formatBytes = (bytes: number, decimals = 2) => {
  if (!+bytes) return '0 Bytes'


  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}

export const getRandomColor = () => {
  return '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');
}

export const getContrastColor = (hexColor: string) => {
  // Convert hex to RGB
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);

  // Calculate brightness
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;

  // Return black or white based on brightness
  return brightness > 120 ? '#000000' : '#FFFFFF';
}

export const getShortName = (lastName: string, firstName: string) => {
  return lastName[0] + firstName[0];
}

export const getUserID = (id: number, type: string) => {
  if (type === 'teacher') {
    return 'T' + id.toString().padStart(9, '0')
  } else if (type === 'parent') {
    return 'P' + id.toString().padStart(9, '0')
  } else if (type === 'student') {
    return 'S' + id.toString().padStart(9, '0')
  }
}

export const letterGradeTypeRecord = (t: any) => {
  return [
    { id: 1, value: 'course', display: t('course') },
    { id: 2, value: 'group', display: t('score_group') },
    { id: 3, value: 'average', display: t('average') }
  ]
}

export const examPeriodRecord = (t: any) => {
  return [
    { id: 1, value: 'Monthly', display: t('monthly') },
    { id: 2, value: 'Semester', display: t('semester') },
    { id: 3, value: 'Yearly', display: t('yearly') }
  ]
};

/**
 * What kind of document a template produces. This is a property of the
 * template itself, and is what callers filter on — the printing flow asks for
 * templates of type 'invoice', for instance.
 *
 * Not to be confused with typeOfTemplateRecord() below, which lists the data
 * sources a Table *block* can bind to ('invoice-line-items',
 * 'payment-summary'…). The Builder's Type field was wired to that list by
 * mistake, so templates were saved with a data-source name as their type and
 * no lookup by type could ever find them.
 *
 * Single source of truth: the Templates page and the Builder both read this.
 */
export const TEMPLATE_TYPES = [
  // Score reports. These are looked up by exact type (see
  // reportRuntime.ts's scoreTemplateTypes), so a report saved as plain
  // 'report' is never found by the score-report or student-report screens.
  { value: 'report-score-student', label: 'Student score report' },
  { value: 'report-score-class', label: 'Class score summary' },
  { value: 'report-score-class-detail', label: 'Class score (detailed)' },
  { value: 'report-course-score-student', label: 'Student course score' },
  { value: 'report-course-score-class', label: 'Class course score' },
  // Finance. Invoices and receipts share one type today.
  { value: 'invoice', label: 'Invoice / Receipt' },
  // ID cards, looked up by exact type from the batch-print dialog.
  { value: 'student-id-card', label: 'Student ID card' },
  { value: 'teacher-id-card', label: 'Teacher ID card' },
  // General-purpose documents with no dedicated lookup.
  { value: 'certificate', label: 'Certificate' },
  { value: 'letter', label: 'Letter' },
  { value: 'form', label: 'Form' },
  { value: 'report', label: 'Report (general)' },
] as const;

export type TemplateType = typeof TEMPLATE_TYPES[number]['value'];

export const isTemplateType = (value: unknown): value is TemplateType =>
  TEMPLATE_TYPES.some((type) => type.value === value);

export const typeOfTemplateRecord = (t: any) => {
  return [
    {
      id: 1,
      value: 'list-student',
      display: t('list_student'),
      columns: [
        { id: 'id', name: t('id') },
        { id: 'student_id', name: t('student_id') },
        { id: 'student_name', name: t('student') },
        { id: 'gender', name: t('gender') },
        { id: 'class_name', name: t('class') },
        { id: 'grade_name', name: t('grade') },
        { id: 'phone_number', name: t('phone_number') },
      ],
    },
    {
      id: 2,
      value: 'report-score-student',
      display: t('report_score_student'),
      columns: [
        { id: 'id', name: t('id') },
        { id: 'course_name', name: t('course') },
        { id: 'score', name: t('score.name') },
        { id: 'average_score', name: t('average_score') },
        { id: 'letter_grade', name: t('letter_grade') },
        { id: 'rank', name: t('rank') },
      ],
    },
    {
      id: 3,
      value: 'report-score-class',
      display: t('report_score_class'),
      columns: [
        { id: 'id', name: t('id') },
        { id: 'student_name', name: t('student') },
        { id: 'total_score', name: t('score.name') },
        { id: 'average_score', name: t('average_score') },
        { id: 'letter_grade', name: t('letter_grade') },
        {
          id: 'rank', name: t('rank')
        }],
    },
    {
      id: 6,
      value: 'report-score-class-detail',
      display: t('report_score_class_detail') || 'Class Score (Detailed)',
      columns: [
        { id: 'id', name: t('id') },
        { id: 'student_code', name: 'អត្តលេខ' },
        { id: 'student_name', name: t('student') },
        { id: 'gender', name: t('gender') },
        { id: 'date_of_birth', name: 'ថ្ងៃខែឆ្នាំកំណើត' },
        { id: '__subjects__', name: 'មុខវិជ្ជា (dynamic)' },
        { id: 'total_score', name: t('score.name') },
        { id: 'average_score', name: t('average_score') },
        { id: 'rank', name: t('rank') },
        { id: 'letter_grade', name: t('letter_grade') },
        { id: 'result', name: 'លទ្ធផល' },
        { id: 'absence_excused', name: 'មប' },
        { id: 'absence_unexcused', name: 'អប' },
        { id: 'absence_total', name: 'សរុបអវត្ត' },
      ],
    },
    {
      id: 4,
      value: 'report-course-score-student',
      display: t('report_course_score_student'),
      columns: [
        { id: 'id', name: t('id') },
        { id: 'student_name', name: t('student') },
        { id: 'gender', name: t('gender') },
        { id: 'total_score', name: t('score.name') },
        { id: 'average_score', name: t('average_score') },
        { id: 'rank', name: t('rank') },
        { id: 'letter_grade', name: t('letter_grade') },
      ]
    },
    {
      id: 5,
      value: 'report-course-score-class',
      display: t('report_course_score_class'),
      columns: [
        { id: 'id', name: t('id') },
        { id: 'student_name', name: t('student') },
        { id: 'gender', name: t('gender') },
        { id: 'total_score', name: t('score.name') },
        { id: 'average_score', name: t('average_score') },
        { id: 'rank', name: t('rank') },
        { id: 'letter_grade', name: t('letter_grade') },
      ]
    },
    {
      id: 6,
      value: 'invoice-line-items',
      display: 'Invoice line items',
      columns: [
        { id: 'id', name: t('id') },
        { id: 'item_name', name: 'Item' },
        { id: 'description', name: t('description') },
        { id: 'quantity', name: 'Qty' },
        { id: 'unit_price', name: 'Unit price' },
        { id: 'amount', name: 'Amount' },
        { id: 'currency', name: 'Currency' },
      ],
    },
    {
      id: 7,
      value: 'payment-summary',
      display: 'Payment summary',
      columns: [
        { id: 'receipt_number', name: 'Receipt #' },
        { id: 'payment_date', name: 'Payment date' },
        { id: 'payment_method', name: 'Method' },
        { id: 'amount_paid', name: 'Paid' },
        { id: 'remaining_balance', name: 'Balance' },
        { id: 'currency', name: 'Currency' },
      ],
    },
    {
      id: 8,
      value: 'attendance-report',
      display: 'Attendance report',
      columns: [
        { id: 'id', name: t('id') },
        { id: 'student_name', name: t('student') },
        { id: 'class_name', name: t('class') },
        { id: 'present_days', name: 'Present' },
        { id: 'absent_days', name: 'Absent' },
        { id: 'late_days', name: 'Late' },
        { id: 'attendance_rate', name: 'Rate' },
      ],
    },
    {
      id: 9,
      value: 'enrollment-summary',
      display: 'Enrollment summary',
      columns: [
        { id: 'student_name', name: t('student') },
        { id: 'parent_name', name: 'Parent' },
        { id: 'class_name', name: t('class') },
        { id: 'grade_name', name: t('grade') },
        { id: 'academic_year', name: 'Academic year' },
        { id: 'enrollment_status', name: 'Status' },
      ],
    },
    {
      id: 10,
      value: 'certificate-list',
      display: 'Certificate list',
      columns: [
        { id: 'certificate_number', name: 'Certificate #' },
        { id: 'student_name', name: t('student') },
        { id: 'course_name', name: t('course') },
        { id: 'completion_date', name: 'Completion date' },
        { id: 'certificate_url', name: 'Verification URL' },
      ],
    },
    {
      id: 11,
      value: 'student-id-card',
      display: 'Student ID Card',
      columns: [
        { id: 'student_id', name: t('student_id') },
        { id: 'full_name', name: t('student') },
        { id: 'gender', name: t('gender') },
        { id: 'class_name', name: t('class') },
        { id: 'grade_name', name: t('grade') },
        { id: 'academic_year', name: 'Academic year' },
        { id: 'card_id', name: 'Card ID' },
        { id: 'expires_at', name: 'Expiry date' },
      ],
    },
    {
      id: 12,
      value: 'teacher-id-card',
      display: 'Teacher ID Card',
      columns: [
        { id: 'teacher_id', name: t('teacher') + ' ID' },
        { id: 'full_name', name: t('teacher') },
        { id: 'gender', name: t('gender') },
        { id: 'position', name: 'Position' },
        { id: 'course_name', name: t('course') },
        { id: 'class_name', name: t('class') },
        { id: 'card_id', name: 'Card ID' },
        { id: 'expires_at', name: 'Expiry date' },
      ],
    }
  ]
}

export const fontFamilies = [
  { label: 'Niradei', value: 'Niradei', value_detail: 'font-Niradei' },
  { label: 'Angkor', value: 'angkor', value_detail: 'font-angkor' },
  { label: 'Moul', value: 'moul', value_detail: 'font-moul' },
  { label: 'Battambang', value: 'battambang', value_detail: 'font-battambang' },
  { label: 'Bayon', value: 'bayon', value_detail: 'font-bayon' },
  { label: 'Bokor', value: 'bokor', value_detail: 'font-bokor' },
  { label: 'Dangrek', value: 'dangrek', value_detail: 'font-dangrek' },
  { label: 'Kdam Thmor', value: 'kdam-thmor', value_detail: 'font-kdam-thmor' },
  { label: 'Siemreap', value: 'siemreap', value_detail: 'font-siemreap' },
  { label: 'Suwannaphum', value: 'suwannaphum', value_detail: 'font-suwannaphum' },
  { label: 'Tacteing', value: 'tacteing', value_detail: 'font-tacteing' },
];

export const fontSizes = (t: any) => {
  return [
    { label: t('fontsize.extra_small'), value: 'xs', value_detail: 'text-xs' },
    { label: t('fontsize.small'), value: 'sm', value_detail: 'text-sm' },
    { label: t('fontsize.normal'), value: 'base', value_detail: 'text-base' },
    { label: t('fontsize.large'), value: 'lg', value_detail: 'text-lg' },
    { label: t('fontsize.extra_large'), value: 'xl', value_detail: 'text-xl' },
    { label: t('fontsize.double_extra_large'), value: '2xl', value_detail: 'text-2xl' },
    { label: t('fontsize.triple_extra_large'), value: '3xl', value_detail: 'text-3xl' },
  ]
};

// Numeric px presets (Microsoft Word style). User may also type custom number.
export const fontSizesPx = [
  8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 28, 32, 36, 48, 60, 72, 96,
];

export const isPxFontSize = (value: string | undefined | null): boolean => {
  if (!value) return false;
  return /^\d+(\.\d+)?px$/.test(value);
};

// Normalize raw user input (e.g. "12", "12px", " 12 ") into a px size string,
// or null if input is invalid.
export const normalizePxFontSize = (input: string | number | null | undefined): string | null => {
  if (input == null) return null;
  const raw = String(input).trim().replace(/px$/i, '');
  const n = Number(raw);
  if (!isFinite(n) || n <= 0 || n > 200) return null;
  return `${n}px`;
};

export const alignments = (t: any) => {
  return [
    { label: t('alignment.left'), value: 'left', icon: AlignLeftIcon },
    { label: t('alignment.center'), value: 'center', icon: AlignCenterIcon },
    { label: t('alignment.right'), value: 'right', icon: AlignRightIcon }
  ]
};

export const fontWeightOptions = (t: any) => {
  return [
    { label: t('fontweight.normal'), value: 'normal' },
    { label: t('fontweight.medium'), value: 'medium' },
    { label: t('fontweight.semibold'), value: 'semibold' },
    { label: t('fontweight.bold'), value: 'bold' },
    { label: t('fontweight.lighter'), value: 'thin' }
  ];
}

export const textDecorationOptions = (t: any) => {
  return [
    { label: t('none'), value: 'no-underline' },
    { label: t('underline'), value: 'underline' },
    { label: t('overline'), value: 'overline' },
    { label: t('line_through'), value: 'line-through' }
  ];
}

export const fontStyleOptions = (t: any) => {
  return [
    { label: t('normal'), value: 'normal' },
    { label: t('italic'), value: 'italic' }
  ];
}

export const getPositionClass = (pos: string) => {
  switch (pos) {
    case 'left': return 'justify-start';
    case 'center': return 'justify-center';
    case 'right': return 'justify-end';
    default: return 'justify-start';
  }
};

export const dividerStyles = (t: any) => {
  return [
    {
      id: 'solid',
      label: t('solid_line'),
      icon: MinusIcon,
      class: 'border-t'
    },
    {
      id: 'dashed',
      label: t('dashed_line'),
      icon: MoreHorizontalIcon,
      class: 'border-t border-dashed'
    },
    {
      id: 'dotted',
      label: t('dotted_line'),
      icon: DotIcon,
      class: 'border-t border-dotted'
    }
  ];
};


export const loadTemplate = async (templateStore: any, templateId: number) => {
  let parameters = {};
  let blocks = []
  try {
    const template: Template = await templateStore.getTemplate(templateId);
    const rawBlocks = template?.blocks ?? [];

    // Prefer per-block position embedded in data.blocks (new API shape)
    const allHavePosition = rawBlocks.length > 0 && rawBlocks.every((b: TemplateBlock) => b.position);

    if (allHavePosition) {
      blocks = [...rawBlocks].sort((a: TemplateBlock, b: TemplateBlock) => {
        const ra = a.position?.row ?? 0;
        const rb = b.position?.row ?? 0;
        if (ra !== rb) return ra - rb;
        return (a.position?.column ?? 0) - (b.position?.column ?? 0);
      });
    } else if (template?.layout?.blocks && template.layout.blocks.some((b: any) => b.id)) {
      // Legacy: layout.blocks keyed by real block id
      const layoutMap = new Map(template.layout.blocks.map((block: any) => [block.id, block.position]));
      const loadedBlocks = rawBlocks.map((block: TemplateBlock) => ({
        ...block,
        position: layoutMap.get(block.id) || {
          row: 0,
          column: 0,
          size: block.size || '3'
        }
      }));
      loadedBlocks.sort((a, b) => {
        if (a.position.row !== b.position.row) {
          return a.position.row - b.position.row;
        }
        return a.position.column - b.position.column;
      });
      blocks = loadedBlocks;
    } else {
      blocks = calculateBlockPositions(rawBlocks);
    }

    if (template?.parameters) {
      parameters = { ...template.parameters };
    }
    return {
      blocks,
      parameters,
      template,
    }

  } catch (error) {
    console.error('Error loading template:', error);
    throw error;
  }
};

export const calculateBlockPositions = (blocks: TemplateBlock[]) => {
  let currentRow = 0;
  let currentColumn = 0;

  return blocks.map(block => {
    const size = parseInt(block.position?.size || block.size || '3');

    // Check if block would exceed row width
    if (currentColumn + size > 12) {
      currentRow++;
      currentColumn = 0;
    }

    const position = {
      row: currentRow,
      column: currentColumn,
      size: size.toString(),
      // Free-form card layers keep their own placement.
      ...(block.position?.layer ? { layer: block.position.layer } : {}),
    };

    currentColumn += size;

    // Reset column count for next row if we hit width limit
    if (currentColumn >= 12) {
      currentRow++;
      currentColumn = 0;
    }

    return {
      ...block,
      position
    };
  });
};

export const localizedApiError = (error: unknown, t: any): string => {
  const mapped = mapBackendError(error);
  return t(mapped.key, mapped.params);
};

export const localizeApiErrorMessage = <T>(error: T, t: any): T => {
  if (error && typeof error === 'object' && 'message' in error) {
    (error as { message: string }).message = localizedApiError(error, t);
  }
  return error;
};

export const handleApiError = (error: any, actionKey: string, t) => {
  console.error(`${actionKey} error:`, error);

  const { toast } = useToast();
  const mapped = mapBackendError(error);
  toast({
    variant: 'destructive',
    title: t(actionKey),
    description: t(mapped.key, mapped.params)
  });
};

export const isEqual = (a: any, b: any): boolean => {
  if (a === b) return true;

  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) {
    return a === b;
  }

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  return keysA.every(key => isEqual(a[key], b[key]));
};

/**
 * Groups an array of objects by a specified key, returning an array of group objects
 * @param array The array to group
 * @param key The key to group by
 * @param itemsKey The key to use for the array of grouped items
 * @returns An array of group objects, each containing the group key and an array of items
 */
export function groupByWithKey<T extends Record<string, any>, K extends keyof T>(
  array: T[],
  key: K,
  itemsKey: string = "items"
): Array<{ [key: string]: any }> {
  // First create an intermediate object for grouping
  const grouped = array.reduce((result: Record<string, T[]>, currentItem: T) => {
    const groupKey = String(currentItem[key]);

    if (!result[groupKey]) {
      result[groupKey] = [];
    }

    result[groupKey].push(currentItem);
    return result;
  }, {});

  // Then convert to the desired format
  return Object.entries(grouped).map(([groupValue, items]) => {
    return {
      [key]: groupValue,
      [itemsKey]: items
    };
  });
}
